import { useState, useEffect } from "react";
import {
  Sidebar,
  SidebarTrigger,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarFooter,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Star, ChevronDown, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";

import { useFavorites } from "./hooks/useFavorites";
import { CompanySwitcher } from "./components/CompanySwitcher";
import { FavoritesSection } from "./components/FavoritesSection";
import { MenuItemWithContext } from "./components/MenuItemWithContext";
import { LeftMenuProps, MenuItem } from "./types";
import { getMenuItems, companies, getMenuConfig } from "./config/menuItems";
import { usePermissionFilteredMenu } from "@/hooks/use-permission-filtered-menu";
import { useWarehouseHierarchyConfig } from "@/hooks/use-warehouse-hierarchy-config";
import { useMode } from "@/providers/ModeProvider";

/**
 * Helper function to render Phase 1 badge for menu items
 * Shows a green circle badge for Phase 1 items
 */
const renderPhaseBadge = (item: MenuItem) => {
  if (!item.phase || item.phase !== "phase1") return null;
  
  return (
    <div 
      className="w-2 h-2 rounded-full bg-green-500 shrink-0"
      title="Phase 1"
    />
  );
};

/**
 * Convert menu item ID to camelCase for openSections state key
 * e.g., "master-data" -> "masterData", "ai-forecasting" -> "aiForecastingAnalytics"
 */
const getStateKey = (id: string): string => {
  // Special cases for backwards compatibility
  const specialCases: Record<string, string> = {
    'master-data': 'masterData',
    'ai': 'aiForecastingAnalytics',
    'analytics': 'analytics',
  };
  
  if (specialCases[id]) {
    return specialCases[id];
  }
  
  // Convert kebab-case to camelCase
  return id.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
};

export function LeftMenuModular({
  activePage,
  onPageChange,
  selectedCompany = "production",
  setSelectedCompany = () => {},
}: LeftMenuProps) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  // Get the mode for warehouse-location-setup screen to determine if we should fetch from API
  const { isScreenLive } = useMode();
  const isWarehouseSetupLive = isScreenLive("warehouse-location-setup");
  
  // Get level 1 label from warehouse hierarchy config
  const { getLevelDisplayName } = useWarehouseHierarchyConfig({ isLive: isWarehouseSetupLive });
  const level1Label = getLevelDisplayName(1);

  // Get dynamic menu items based on feature flags and custom labels
  const baseMenuItems = getMenuItems({ level1Label });
  const menuConfig = getMenuConfig();
  
  // Filter menu items based on user permissions
  const menuItems = usePermissionFilteredMenu(baseMenuItems);
  
  console.log("📋 Menu Items Loaded:", menuItems.map(m => m.id));
  
  // Initialize openSections state dynamically based on menu items
  const initialOpenSections = menuItems.reduce((acc, item) => {
    if (item.children && item.children.length > 0) {
      acc[getStateKey(item.id)] = false;
    }
    return acc;
  }, {} as Record<string, boolean>);
  
  // Track which parent sections are open
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(initialOpenSections);

  const { state, open, setOpen, isMobile, openMobile, setOpenMobile } = useSidebar();
  const isCollapsed = state === "collapsed";

  const favoritesEnabled = menuConfig.totalPages >= menuConfig.favoritesThreshold;
  const { favorites, toggleFavorite, isFavorite } = useFavorites(favoritesEnabled);

  // Get all items including children for favorites
  const allItems = menuItems.flatMap((item) => [item, ...(item.children || [])]);
  const favoriteItems = allItems.filter((item) => isFavorite(item.id));

  // Filter menu items based on search query
  const filterMenuItems = (items: typeof menuItems, query: string) => {
    if (!query.trim()) return items;
    
    const lowerQuery = query.toLowerCase();
    
    return items.map((item) => {
      // Check if parent label matches
      const parentMatches = item.label.toLowerCase().includes(lowerQuery);
      
      // Filter children that match
      const matchingChildren = item.children?.filter((child) =>
        child.label.toLowerCase().includes(lowerQuery)
      );
      
      // Include item if parent matches or has matching children
      if (parentMatches || (matchingChildren && matchingChildren.length > 0)) {
        return {
          ...item,
          children: parentMatches ? item.children : matchingChildren,
        };
      }
      
      return null;
    }).filter(Boolean) as typeof menuItems;
  };

  const filteredMenuItems = filterMenuItems(menuItems, searchQuery);

  // Set active item based on current page
  useEffect(() => {
    const findItemId = (page: string): string | null => {
      // Check top-level items (exact match)
      const topLevelItem = menuItems.find((item) => item.page === page);
      if (topLevelItem) {
        return `main-${topLevelItem.id}`;
      }
      
      // Check children (exact match)
      for (const parent of menuItems) {
        if (parent.children) {
          const childItem = parent.children.find((child) => child.page === page);
          if (childItem) {
            return `main-${childItem.id}`;
          }
        }
      }
      
      // Check for sub-pages (e.g., "stock-takes/targeted/setup" should match "stock-takes")
      // This handles navigation to sub-pages that should highlight their parent menu item
      for (const parent of menuItems) {
        if (parent.children) {
          const childItem = parent.children.find((child) => page.startsWith(child.page + "/"));
          if (childItem) {
            return `main-${childItem.id}`;
          }
        }
      }
      
      return null;
    };
    
    const itemId = findItemId(activePage);
    if (itemId) {
      setActiveItemId(itemId);
    }
  }, [activePage, menuItems]);

  const handleItemClick = (page: string, itemId: string, parentSection?: string) => {
    // Store the current collapsed state before any changes
    const wasCollapsed = isCollapsed;
    
    // If sidebar is collapsed, expand it
    if (wasCollapsed) {
      if (isMobile) {
        setOpenMobile(true);
      } else {
        setOpen(true);
      }
    }
    
    // If this is a nested item and sidebar was collapsed, open its parent section
    if (wasCollapsed && parentSection) {
      // Use setTimeout to ensure the sidebar expansion happens first
      setTimeout(() => {
        setOpenSections((prev) => ({ ...prev, [parentSection]: true }));
      }, 50);
    }
    
    setActiveItemId(itemId);
    onPageChange(page);
  };

  const renderMenuItem = (item: any, uniqueId: string, isFavoriteItem: boolean = false) => {
    const isActive = activeItemId === uniqueId;
    return (
      <MenuItemWithContext
        item={item}
        uniqueId={uniqueId}
        isActive={isActive}
        isFavoriteItem={isFavoriteItem}
        isFavorite={isFavorite(item.id)}
        onItemClick={handleItemClick}
        onToggleFavorite={toggleFavorite}
        favoritesEnabled={favoritesEnabled}
      />
    );
  };

  /**
   * Dynamically render a menu section (with or without children)
   */
  const renderMenuSection = (item: MenuItem) => {
    const stateKey = getStateKey(item.id);
    const Icon = item.icon;
    
    // If item has no children, render as a simple menu item
    if (!item.children || item.children.length === 0) {
      return (
        <SidebarMenuItem key={item.id}>
          {renderMenuItem(item, `main-${item.id}`)}
        </SidebarMenuItem>
      );
    }
    
    // If item has children, render as a collapsible section
    return (
      <Collapsible 
        key={item.id}
        asChild 
        open={openSections[stateKey]}
        onOpenChange={(open) => setOpenSections((prev) => ({ ...prev, [stateKey]: open }))}
        className="group/collapsible"
      >
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton 
              tooltip={item.label}
              onClick={() => {
                // If collapsed, expand sidebar and open this section
                if (isCollapsed) {
                  if (isMobile) {
                    setOpenMobile(true);
                  } else {
                    setOpen(true);
                  }
                  setTimeout(() => {
                    setOpenSections((prev) => ({ ...prev, [stateKey]: true }));
                  }, 50);
                }
              }}
              className="flex items-center gap-2"
            >
              <Icon />
              <span className="flex-1 flex items-center gap-1.5">
                {renderPhaseBadge(item)}
                {item.label}
              </span>
              <ChevronDown className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {item.children.map((child) => (
                <SidebarMenuSubItem key={child.id}>
                  {favoritesEnabled ? (
                    <ContextMenu>
                      <ContextMenuTrigger asChild>
                        <SidebarMenuSubButton
                          onClick={() => handleItemClick(child.page, `main-${child.id}`, stateKey)}
                          isActive={activeItemId === `main-${child.id}`}
                          className="flex items-center gap-2"
                        >
                          <span className="flex-1 flex items-center gap-1.5">
                            {renderPhaseBadge(child)}
                            {child.label}
                          </span>
                        </SidebarMenuSubButton>
                      </ContextMenuTrigger>
                      <ContextMenuContent>
                        <ContextMenuItem onClick={() => toggleFavorite(child.id)}>
                          <Star className={isFavorite(child.id) ? "fill-current" : ""} />
                          {isFavorite(child.id) ? "Remove from favorites" : "Set as favorite"}
                        </ContextMenuItem>
                      </ContextMenuContent>
                    </ContextMenu>
                  ) : (
                    <SidebarMenuSubButton
                      onClick={() => handleItemClick(child.page, `main-${child.id}`, stateKey)}
                      isActive={activeItemId === `main-${child.id}`}
                      className="flex items-center gap-2"
                    >
                      <span className="flex-1 flex items-center gap-1.5">
                        {renderPhaseBadge(child)}
                        {child.label}
                      </span>
                    </SidebarMenuSubButton>
                  )}
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  };

  return (
    <Sidebar variant="floating" collapsible="icon" className="sticky top-0 h-screen">
      {/* Header */}
      <SidebarHeader className="pt-2 flex-shrink-0">
        <div className="flex items-center gap-2 pl-2">
          <SidebarTrigger />
          <h3 className="font-semibold truncate whitespace-nowrap max-w-[140px] group-data-[state=collapsed]:hidden text-neutral-700">
            Stock Evo
          </h3>
          <div className="ml-auto mr-2 group-data-[state=collapsed]:hidden">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsSearchOpen(!isSearchOpen)}
            >
              {isSearchOpen ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </SidebarHeader>
      <div className="border-t border-neutral-200 mx-2 group-data-[state=collapsed]:show flex-shrink-0"></div>
      
      {/* Search Input */}
      {isSearchOpen && (
        <div className="px-3 py-2 group-data-[state=collapsed]:hidden">
          <Input
            type="text"
            placeholder="Search pages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8"
            autoFocus
          />
        </div>
      )}

      {/* Content */}
      <SidebarContent className="group-data-[state=collapsed]:items-center flex-1 overflow-y-auto">
        {/* Favorites Section */}
        {favoritesEnabled && (
          <FavoritesSection favoriteItems={favoriteItems} renderMenuItem={renderMenuItem} />
        )}

        {/* Main Navigation Section - DYNAMICALLY RENDERED */}
        <SidebarGroup className="group-data-[state=collapsed]:items-center">
          <SidebarGroupContent className="group-data-[state=collapsed]:w-8">
            <SidebarMenu className="gap-0.5 group-data-[state=collapsed]:w-full [&_span]:transition-all [&_span]:duration-200 [&_span]:ease-out [&_span]:opacity-100 [&_span]:translate-x-0 group-data-[state=collapsed]:[&_span]:opacity-0 group-data-[state=collapsed]:[&_span]:-translate-x-2 group-data-[state=collapsed]:[&_span]:pointer-events-none">
              
              {/* Dynamically render all menu sections */}
              {filteredMenuItems.map((item, index) => (
                <>
                  {renderMenuSection(item)}
                  {/* Add divider after Quick Start */}
                  {item.id === 'quick-start' && (
                    <div className="border-t border-neutral-200 my-2 mx-2" />
                  )}
                </>
              ))}

            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="flex-shrink-0">
        {/* Company Switcher */}
        {menuConfig.showDatabaseSwitcher && (
          <>
            <div className="border-t border-neutral-200 mb-2 mt-2 group-data-[state=collapsed]:hidden"></div>
            <CompanySwitcher
              companies={companies}
              selectedCompany={selectedCompany}
              onCompanyChange={setSelectedCompany}
              label="Company"
            />
          </>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
